import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuIcon from '@mui/icons-material/Menu';
import GroupsIcon from '@mui/icons-material/Groups';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArticleIcon from '@mui/icons-material/Article';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import TimelineIcon from '@mui/icons-material/Timeline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import { purple, blue } from '@mui/material/colors';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { toggleTheme, setSelectedTheme, THEME_MODE_KEY, THEME_ID_KEY } from '../../store/slices/themeSlice';
import { toggleDeveloperMode } from '../../store/slices/developerModeSlice';
import { setPods } from '../../store/slices/podsSlice';
import { setEpics } from '../../store/slices/epicsSlice';
import type { Epic } from '../../store/slices/epicsSlice';
import type { Workstream } from '../../store/slices/workstreamsSlice';
import { setCategories } from '../../store/slices/categoriesSlice';
import { setSprintSettings, DEFAULT_SPRINT_LENGTH, DEFAULT_TOTAL_SPRINTS } from '../../store/slices/sprintsSlice';
import type { WorkstreamSprint } from '../../store/slices/sprintsSlice';
import { validateEpicExportData, EpicExportData } from '../../utils/epicConfigValidation';
import { appThemes } from '../../themes';
import ScenarioPickerDialog from '../ScenarioPickerDialog/ScenarioPickerDialog';

const DRAWER_WIDTH = 260;

const navItems = [
  { label: 'Epic Planner', path: '/epic-planner', icon: <AssignmentIcon /> },
  { label: 'Workstream Forecaster', path: '/workstream-forecaster', icon: <TimelineIcon /> },
  { label: 'Backlog', path: '/backlog', icon: <FormatListBulletedIcon /> },
  { label: 'Pod Management', path: '/pod-management', icon: <GroupsIcon /> },
];

const docNavItem = { label: 'Documentation', path: '/documentation', icon: <MenuBookIcon /> };
const settingsNavItem = { label: 'Settings', path: '/settings', icon: <TuneIcon /> };

interface LayoutProps {
  children: React.ReactNode;
}

/** Maximum characters shown per breadcrumb label before truncating with an ellipsis. */
const LABEL_MAX_CHARS = 30;
/** Maximum pixel width of each breadcrumb item before the browser clips overflow. */
const CRUMB_MAX_WIDTH = 220;
/** Shared sx styles that make a span show text-overflow ellipsis inside a flex container. */
const TRUNCATE_TEXT_SX = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 } as const;

const truncateLabel = (str: string): string =>
  str.length > LABEL_MAX_CHARS ? str.slice(0, LABEL_MAX_CHARS - 1) + '\u2026' : str;

const getBreadcrumbs = (pathname: string, epics: Epic[], workstreams: Workstream[], sprints: WorkstreamSprint[], from?: string) => {
  type Crumb = { label: string; path?: string; icon?: React.ReactNode };

  // choose the main section that matches the current pathname; fallback to first nav item
  const allNavItems = [...navItems, settingsNavItem, docNavItem];
  const section = (from ? allNavItems.find((n) => n.path === from) : null)
    ?? allNavItems.find((n) => pathname.startsWith(n.path))
    ?? navItems[0];

  const crumbs: Crumb[] = [];
  if (section) {
    crumbs.push({ label: section.label, path: section.path });
  }

  if (pathname.startsWith('/pod-management/new')) {
    crumbs.push({ label: 'Create Pod' });
  } else if (pathname.match(/\/pod-management\/.+\/edit/)) {
    crumbs.push({ label: 'Edit Pod' });
  } else if (pathname.startsWith('/epic-planner/workstreams/new')) {
    crumbs.push({ label: 'Create Workstream' });
  } else if (pathname.match(/\/epic-planner\/workstreams\/([^/]+)\/sprints\/([^/]+)\/edit/)) {
    const match = pathname.match(/\/epic-planner\/workstreams\/([^/]+)\/sprints\/([^/]+)\/edit/)!;
    const wsId = match[1];
    const sprintId = match[2];
    const ws = workstreams.find((w) => w.id === wsId);
    const sprint = sprints.find((s) => s.id === sprintId);
    const sprintLabel = sprint
      ? `Sprint ${sprint.number}${sprint.nickname ? ` · ${sprint.nickname}` : ''}`
      : 'Sprint';
    crumbs.push({
      label: ws ? truncateLabel(ws.name) : 'Workstream',
      path: wsId ? `/epic-planner/workstreams/${wsId}/edit` : undefined,
    });
    crumbs.push({ label: truncateLabel(sprintLabel) });
  } else if (pathname.match(/\/epic-planner\/workstreams\/.+\/edit/)) {
    crumbs.push({ label: 'Edit Workstream' });
  } else if (pathname.startsWith('/epic-planner/new')) {
    crumbs.push({ label: 'New Epic', icon: <AssignmentIcon fontSize="small" sx={{ color: purple[500] }} /> });
  } else if (pathname.match(/\/epic-planner\/.+\/edit/)) {
    const epicId = pathname.match(/\/epic-planner\/([^/]+)\/edit/)?.[1];
    const epic = epics.find((e) => e.id === epicId);
    crumbs.push({
      label: epic ? truncateLabel(epic.title) : 'Edit Epic',
      icon: <AssignmentIcon fontSize="small" sx={{ color: purple[500] }} />,
    });
  } else if (pathname.match(/\/epic-planner\/workstreams\/.+\/sprint-planner/)) {
    const wsId = pathname.match(/\/epic-planner\/workstreams\/([^/]+)\/sprint-planner/)?.[1];
    const ws = workstreams.find((w) => w.id === wsId);
    crumbs.push({
      label: ws ? truncateLabel(ws.name) : 'Workstream',
      path: wsId ? `/epic-planner/workstreams/${wsId}/edit` : undefined,
    });
    crumbs.push({ label: 'Sprint Planner' });
  } else if (pathname.startsWith('/backlog/stories/new')) {
    crumbs.push({
      label: 'New Story',
      icon: <ArticleIcon fontSize="small" sx={{ color: blue[500] }} />,
    });
  } else if (pathname.match(/\/backlog\/stories\/.+\/edit/)) {
    const storyId = pathname.match(/\/backlog\/stories\/([^/]+)\/edit/)?.[1];
    let parentEpic: Epic | undefined;
    let storyTitle: string | undefined;
    for (const epic of epics) {
      const found = (epic.stories ?? []).find((s) => s.id === storyId);
      if (found) {
        parentEpic = epic;
        storyTitle = found.title;
        break;
      }
    }
    if (parentEpic) {
      crumbs.push({
        label: truncateLabel(parentEpic.title),
        path: `/backlog/epics/${parentEpic.id}/edit`,
        icon: <AssignmentIcon fontSize="small" sx={{ color: purple[500] }} />,
      });
    } else {
      crumbs.push({ label: 'Stories' });
    }
    crumbs.push({
      label: storyTitle ? truncateLabel(storyTitle) : 'Edit Story',
      icon: <ArticleIcon fontSize="small" sx={{ color: blue[500] }} />,
    });
  } else if (pathname.startsWith('/backlog/epics/new')) {
    crumbs.push({
      label: 'New Epic',
      icon: <AssignmentIcon fontSize="small" sx={{ color: purple[500] }} />,
    });
  } else if (pathname.match(/\/backlog\/epics\/.+\/edit/)) {
    const epicId = pathname.match(/\/backlog\/epics\/([^/]+)\/edit/)?.[1];
    const epic = epics.find((e) => e.id === epicId);
    crumbs.push({
      label: epic ? truncateLabel(epic.title) : 'Edit Epic',
      icon: <AssignmentIcon fontSize="small" sx={{ color: purple[500] }} />,
    });
  }

  return crumbs;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const themeMode = useAppSelector((state) => state.theme.mode);
  const selectedThemeId = useAppSelector((state) => state.theme.selectedThemeId);
  const epics = useAppSelector((state) => state.epics.epics);
  const workstreams = useAppSelector((state) => state.workstreams.workstreams);
  const workstreamSprints = useAppSelector((state) => state.sprints.workstreamSprints);
  const categories = useAppSelector((state) => state.categories.categories);
  const developerMode = useAppSelector((state) => state.developerMode.developerMode);
  const breadcrumbs = getBreadcrumbs(location.pathname, epics, workstreams, workstreamSprints, (location.state as { from?: string } | null)?.from);

  const handleOpenScenarioDialog = () => {
    setSettingsOpen(false);
    setScenarioDialogOpen(true);
  };

  const handleClearConfig = () => {
    dispatch(setPods([]));
    dispatch(setEpics([]));
    const nextMonday = new Date();
    const day = nextMonday.getDay();
    const diff = day === 1 ? 0 : (8 - day) % 7;
    nextMonday.setDate(nextMonday.getDate() + diff);
    nextMonday.setHours(0, 0, 0, 0);
    dispatch(setSprintSettings({ startDate: nextMonday.toISOString(), sprintLength: DEFAULT_SPRINT_LENGTH, totalSprints: DEFAULT_TOTAL_SPRINTS }));
    try {
      localStorage.removeItem(THEME_MODE_KEY);
      localStorage.removeItem(THEME_ID_KEY);
    } catch {
      // ignore storage errors
    }
    setSettingsOpen(false);
  };

  const handleExportEpics = () => {
    const data: EpicExportData = { version: '1.0', epics, categories };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'epic-configuration.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportEpics = (event: React.ChangeEvent<HTMLInputElement>) => {
    setImportError(null);
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data: unknown = JSON.parse(e.target?.result as string);
        if (!validateEpicExportData(data)) {
          setImportError('Invalid schema. Please check your JSON file and try again.');
          return;
        }
        dispatch(setCategories(data.categories));
        dispatch(setEpics(data.epics));
        setSettingsOpen(false);
      } catch {
        setImportError('Failed to parse JSON file. Please ensure the file is valid JSON.');
      }
    };
    reader.readAsText(file);
    // Reset input to allow re-importing the same file
    event.target.value = '';
  };

  const handleImportClick = () => {
    setImportError(null);
    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 2 }}
            aria-label="menu"
          >
            <MenuIcon />
          </IconButton>
          <Breadcrumbs aria-label="breadcrumb" sx={{ color: 'inherit', flexGrow: 1 }}>
            {breadcrumbs.map((crumb, i) =>
              i < breadcrumbs.length - 1 && crumb.path ? (
                <Link
                  key={i}
                  color="inherit"
                  href={crumb.path}
                  onClick={(e) => { e.preventDefault(); navigate(crumb.path!); }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', maxWidth: CRUMB_MAX_WIDTH }}
                >
                  {crumb.icon}
                  <Box component="span" sx={TRUNCATE_TEXT_SX}>
                    {crumb.label}
                  </Box>
                </Link>
              ) : (
                <Typography
                  key={i}
                  color="inherit"
                  fontWeight={i === breadcrumbs.length - 1 ? 600 : 400}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: CRUMB_MAX_WIDTH }}
                >
                  {crumb.icon}
                  <Box component="span" sx={TRUNCATE_TEXT_SX}>
                    {crumb.label}
                  </Box>
                </Typography>
              )
            )}
          </Breadcrumbs>
          <IconButton
            color="inherit"
            edge="end"
            onClick={() => setSettingsOpen(true)}
            aria-label="settings"
          >
            <SettingsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', pt: 8, display: 'flex', flexDirection: 'column' } }}
      >
        {[
          { items: navItems, grow: true },
          { items: [settingsNavItem, docNavItem], grow: false },
        ].map(({ items, grow }, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {groupIndex > 0 && <Divider />}
            <List>
              {items.map((item) => (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    selected={location.pathname.startsWith(item.path)}
                    onClick={() => { navigate(item.path); setDrawerOpen(false); }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {grow && <Box sx={{ flexGrow: 1 }} />}
          </React.Fragment>
        ))}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: '64px',
          p: 3,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {children}
      </Box>
      <Box
        component="footer"
        sx={{
          height: '30px',
          bgcolor: themeMode === 'dark' ? 'background.paper' : 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2">© {new Date().getFullYear()} Team Capacity Planner</Typography>
      </Box>

      {/* Settings Modal */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ position: 'relative' }}>
          App Settings
          <IconButton
            aria-label="close"
            onClick={() => setSettingsOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <FormControlLabel
            control={
              <Switch
                checked={themeMode === 'dark'}
                onChange={() => dispatch(toggleTheme())}
                color="primary"
              />
            }
            label={themeMode === 'dark' ? 'Dark Mode' : 'Light Mode'}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Themes
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1,
              mt: 1,
            }}
          >
            {appThemes.map((t) => {
              const colors = themeMode === 'dark' ? t.dark : t.light;
              const isSelected = selectedThemeId === t.id;
              return (
                <Tooltip key={t.id} title={t.name} placement="top">
                  <Box
                    onClick={() => dispatch(setSelectedTheme(t.id))}
                    aria-label={`Select ${t.name} theme`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        dispatch(setSelectedTheme(t.id));
                      }
                    }}
                    sx={{
                      position: 'relative',
                      borderRadius: 1,
                      border: isSelected ? '2px solid' : '2px solid transparent',
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      cursor: 'pointer',
                      p: 0.75,
                      bgcolor: 'background.paper',
                      boxShadow: isSelected ? 2 : 1,
                      '&:hover': { boxShadow: 3 },
                      outline: 'none',
                    }}
                  >
                    {isSelected && (
                      <CheckCircleIcon
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          fontSize: 14,
                          color: 'primary.main',
                        }}
                      />
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: colors.primary }} />
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: colors.secondary }} />
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: colors.backgroundDefault, border: '1px solid', borderColor: 'divider' }} />
                    </Box>
                    <Typography variant="caption" noWrap sx={{ display: 'block', fontSize: '0.65rem' }}>
                      {t.name}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
          <Divider sx={{ my: 2 }} />
          {/* <Typography variant="subtitle2" gutterBottom>
            Background Design
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1,
              mt: 1,
            }}
          >
            {backgroundDesigns.map((bg) => {
              const isSelected = selectedBackgroundId === bg.id;
              return (
                <Tooltip key={bg.id} title={bg.name} placement="top">
                  <Box
                    onClick={() => dispatch(setSelectedBackground(bg.id))}
                    aria-label={`Select ${bg.name} background`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        dispatch(setSelectedBackground(bg.id));
                      }
                    }}
                    sx={{
                      position: 'relative',
                      borderRadius: 1,
                      border: isSelected ? '2px solid' : '2px solid transparent',
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      cursor: 'pointer',
                      p: 0.75,
                      bgcolor: 'background.paper',
                      boxShadow: isSelected ? 2 : 1,
                      '&:hover': { boxShadow: 3 },
                      outline: 'none',
                    }}
                  >
                    {isSelected && (
                      <CheckCircleIcon
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          fontSize: 14,
                          color: 'primary.main',
                          zIndex: 1,
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        width: '100%',
                        height: 36,
                        borderRadius: 0.5,
                        overflow: 'hidden',
                        mb: 0.5,
                        bgcolor: themeMode === 'dark' ? 'grey.900' : 'grey.100',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {bg.id === 'none' ? (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        </Box>
                      ) : (
                        <BackgroundPreview
                          designId={bg.id}
                          primary={currentThemeColors.primary}
                          secondary={currentThemeColors.secondary}
                          isDark={themeMode === 'dark'}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" noWrap sx={{ display: 'block', fontSize: '0.65rem' }}>
                      {bg.name}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
          */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Developer Mode
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={developerMode}
                onChange={() => dispatch(toggleDeveloperMode())}
                color="warning"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CodeIcon sx={{ fontSize: '1.1rem', color: developerMode ? 'warning.main' : 'text.secondary' }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: developerMode ? 600 : 400,
                    color: developerMode ? 'warning.main' : 'text.primary',
                  }}
                >
                  {developerMode ? 'Developer Mode Active' : 'Developer Mode Off'}
                </Typography>
              </Box>
            }
          />
          {developerMode && (
            <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Testing &amp; Demo
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleOpenScenarioDialog}
            >
              Load Demo Configuration
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={handleClearConfig}
            >
              Clear Configuration
            </Button>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Epic &amp; Story Configuration
          </Typography>
          {importError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setImportError(null)}>
              {importError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={handleExportEpics}
            >
              Export Epic &amp; Story Details
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleImportClick}
            >
              Import Epic Configuration
            </Button>
            <input
              type="file"
              accept=".json,application/json"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleImportEpics}
            />
          </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ScenarioPickerDialog open={scenarioDialogOpen} onClose={() => setScenarioDialogOpen(false)} />
    </Box>
  );
};

export default Layout;
